import { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { searchPlaces, PlaceSearchResult } from "@/Api/maps";
import { Search, MapPin, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface AddressSearchProps {
  onAddressSelect: (address: {
    addressName: string;
    street: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
    zip_code: string;
    latitude: string;
    longitude: string;
  }) => void;
  placeholder?: string;
  className?: string;
  defaultValue?: string;
  autoSearchOnMount?: boolean;
}

export const AddressSearch = ({
  onAddressSelect,
  placeholder = "Buscar endereço...",
  className,
  defaultValue = "",
  autoSearchOnMount = false,
}: AddressSearchProps) => {
  const [query, setQuery] = useState(defaultValue);
  const [results, setResults] = useState<PlaceSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchTimeout = useRef<NodeJS.Timeout>();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAddressSelect = useCallback(
    (place: PlaceSearchResult) => {
      // Parse do endereço do Google Maps
      // Formato típico: "Rua Frei Odilon, 68 - Álvaro Weyne, Fortaleza - CE, 60336-190, Brazil"
      const fullAddress = place.address;
      const addressParts = fullAddress.split(", ");

      let street = "";
      let number = "";
      let neighborhood = "";
      let city = "";
      let state = "";
      let zip_code = "";

      // Tenta extrair as partes do endereço
      if (addressParts.length >= 1) {
        // Primeira parte geralmente é "Rua X, 123" ou "Rua X - Bairro"
        const firstPart = addressParts[0];

        // Verifica se tem número separado por vírgula ou traço
        if (firstPart.includes(" - ")) {
          const [streetWithNumber, bairro] = firstPart.split(" - ");
          // Extrai número se existir
          const streetMatch = streetWithNumber.match(/^(.+?),?\s*(\d+)?$/);
          if (streetMatch) {
            street = streetMatch[1].trim();
            number = streetMatch[2] || "";
          } else {
            street = streetWithNumber.trim();
          }
          neighborhood = bairro?.trim() || "";
        } else {
          // Tenta extrair número do final
          const streetMatch = firstPart.match(/^(.+?),?\s*(\d+)$/);
          if (streetMatch) {
            street = streetMatch[1].trim();
            number = streetMatch[2] || "";
          } else {
            street = firstPart.trim();
          }
        }
      }

      // Segunda parte pode ser "número - bairro", só número, bairro ou cidade
      if (addressParts.length >= 2 && !neighborhood) {
        // Verifica se tem " - " separador
        if (addressParts[1].includes(" - ")) {
          const [primeiraParte, segundaParte] = addressParts[1].split(" - ");
          // Se a primeira parte é um número e ainda não temos número, é "número - bairro"
          if (/^\d+[A-Za-z]?$/.test(primeiraParte.trim()) && !number) {
            number = primeiraParte.trim();
            neighborhood = segundaParte?.trim() || "";
          }
          // Se o segundo elemento parece um estado (2 letras), então o primeiro é cidade
          else if (segundaParte && segundaParte.trim().length === 2) {
            city = primeiraParte.trim();
            state = segundaParte.trim();
          } else {
            // Caso contrário, primeira parte é bairro
            neighborhood = primeiraParte.trim();
          }
        } else {
          // Se a segunda parte é apenas um número e ainda não temos número
          if (/^\d+[A-Za-z]?$/.test(addressParts[1].trim()) && !number) {
            number = addressParts[1].trim();
          } else {
            neighborhood = addressParts[1].trim();
          }
        }
      }

      // Terceira parte pode ser bairro (se ainda não temos), "Cidade - Estado" ou cidade
      if (addressParts.length >= 3) {
        if (addressParts[2].includes(" - ")) {
          const [cidadePart, estadoPart] = addressParts[2].split(" - ");
          city = cidadePart.trim();
          state = estadoPart.trim();
        } else if (!neighborhood) {
          // Se ainda não temos bairro, a terceira parte é o bairro
          neighborhood = addressParts[2].trim();
        } else if (!city) {
          city = addressParts[2].trim();
        }
      }

      // Quarta parte pode ser "Cidade - Estado" (se bairro foi encontrado na terceira)
      if (addressParts.length >= 4 && !city) {
        if (addressParts[3].includes(" - ")) {
          const [cidadePart, estadoPart] = addressParts[3].split(" - ");
          city = cidadePart.trim();
          state = estadoPart.trim();
        } else if (!/^\d{5}-?\d{3}$/.test(addressParts[3].trim())) {
          // Se não é CEP, é cidade
          city = addressParts[3].trim();
        }
      }

      // Busca CEP (formato brasileiro: 5 dígitos + hífen + 3 dígitos ou 8 dígitos)
      const cepMatch = fullAddress.match(/\d{5}-?\d{3}/);
      if (cepMatch) {
        zip_code = cepMatch[0];
      }

      // Busca estado se ainda não encontrou (formato "State of X" ou sigla de 2 letras)
      if (!state) {
        const stateMatch = addressParts.find((part) =>
          part.includes("State of") ||
          (part.trim().length === 2 && /^[A-Z]{2}$/.test(part.trim()))
        );
        if (stateMatch) {
          state = stateMatch.replace("State of ", "").trim();
        }
      }

      // Se não conseguiu extrair cidade, usa o nome do lugar
      if (!city) {
        city = place.name || "";
      }

      const address = {
        addressName: place.address,
        street,
        number,
        neighborhood,
        city,
        state,
        zip_code,
        latitude: place.latitude.toString(),
        longitude: place.longitude.toString(),
      };

      console.log('[AddressSearch] Parsed address:', address);

      onAddressSelect(address);
      setQuery(place.address);
      setIsOpen(false);
      setSelectedIndex(-1);
      setResults([]);
    },
    [onAddressSelect]
  );

  const performSearch = useCallback(
    async (searchQuery: string, autoSelectFirst: boolean = false) => {
      if (!searchQuery.trim() || searchQuery.length < 3) {
        setResults([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await searchPlaces(`${searchQuery}`);
        if (response.ok && response.data?.response?.response) {
          const places = response.data.response.response;
          setResults(places);
          setIsOpen(places.length > 0 && !autoSelectFirst);
          setSelectedIndex(-1);

          if (autoSelectFirst && places.length > 0) {
            console.log(
              "AddressSearch: Auto-selecting first result:",
              places[0]
            );
            handleAddressSelect(places[0]);
          }
        } else {
          setResults([]);
          setIsOpen(false);
        }
      } catch (error) {
        console.error("Error searching places:", error);
        setResults([]);
        setIsOpen(false);
      } finally {
        setIsLoading(false);
      }
    },
    [handleAddressSelect]
  );

  useEffect(() => {
    if (autoSearchOnMount && defaultValue && defaultValue.trim().length >= 3) {
      performSearch(defaultValue, true); // Auto-select first result
    }
  }, [defaultValue, autoSearchOnMount, performSearch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleAddressSelect(results[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      <div className="relative">
        <Input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) {
              setIsOpen(true);
            }
          }}
          className="pr-10"
        />
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          ) : (
            <Search className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>

      {isOpen && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {results.map((place, index) => (
            <Button
              key={place.place_id}
              variant="ghost"
              className={cn(
                "w-full justify-start px-3 py-2 text-left hover:bg-gray-50 rounded-none border-none h-auto",
                selectedIndex === index && "bg-gray-50"
              )}
              onClick={() => handleAddressSelect(place)}
            >
              <MapPin className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {place.name}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {place.address}
                </div>
              </div>
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};
